import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

import IconSearchNotFound from "../../assets/icons/icons8-search-notfound.png";
import IconWeatherChuvaIntensa from "../../assets/icons/icons-weather-chuva-intensa.png";
import IconWeatherDiaParcialmenteNublado from "../../assets/icons/icons-weather-dia-parcialmente-nublado.png";
import IconWeatherNoiteParcialmenteNublada from "../../assets/icons/icons-weather-noite-parcialmente-nublada.png";
import IconWeatherNuvens from "../../assets/icons/icons-weather-nuvens.png";
import IconWeatherParcialmenteNubladoChuva from "../../assets/icons/icons-weather-parcialmente-nublado-chuva.png";
import IconWeatherSol from "../../assets/icons/icons-weather-sol.png";
import IconWeatherVento from "../../assets/icons/icons-weather-vento.png";

import { apiprevmet3 } from "../../services/inmet_api.js";
import { WEATHER_API_KEY } from "../../services/apiKeys.js";

import * as S from "./styles.js";
import { theme } from "../../styles/theme.js";
import { Auxiliar, BodyP, TaglineP } from "../../styles/stylesTypography.js";
import RainForecastGraphic from "../nova-farm/rain-forecast-graphic/index.js";

const GraphicPreview = ({
  coordenadas,
  codIBGE,
  location,
  weatherData: weatherDataProp,
  // Endereço completo da fazenda (já montado em pages/nova-farm/index.js, a
  // partir do mesmo objeto usado em farm-header/index.js) - exibido acima dos
  // cards diários, pra deixar claro de qual propriedade é a previsão.
  address,
  // Renderizado logo após a fileira de cards de condições climáticas (dailyForecast)
  // e antes dos gráficos de temperatura/chuva - usado pelo semáforo climático em
  // pages/nova-farm/index.js, que precisava ficar nessa posição específica.
  afterDailyCards,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [weatherDataInternal, setWeatherDataInternal] = useState(null);
  const [inmetData, setInmetData] = useState(null);
  // No mobile (<769px) os rótulos do eixo dos gráficos mudam: a cada 6h
  // (00h/06h/12h/18h, ver formatChartLabel) em vez de a cada 3h com o dia da
  // semana na virada - tira+gráficos deixaram de rolar horizontal nessa
  // largura (ver styles.js), então precisam de menos rótulos pra não
  // amontoar no eixo.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 769
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Se um weatherData foi passado como prop, usa ele; senão usa o estado interno
  const weatherData = weatherDataProp !== undefined ? weatherDataProp : weatherDataInternal;

  // Mapeamento de ícones do clima baseado nos códigos do OpenWeatherMap
  const getWeatherIcon = (weatherCode, hour) => {
    const isDayTime = hour >= 6 && hour < 18;
    
    // Lógica baseada nos códigos da API OpenWeatherMap
    // https://openweathermap.org/weather-conditions
    const iconMap = {
      // Clear
      '01d': IconWeatherSol,
      '01n': IconWeatherSol,
      
      // Few clouds
      '02d': IconWeatherDiaParcialmenteNublado,
      '02n': IconWeatherNoiteParcialmenteNublada,
      
      // Scattered clouds
      '03d': IconWeatherNuvens,
      '03n': IconWeatherNuvens,
      
      // Broken clouds
      '04d': IconWeatherNuvens,
      '04n': IconWeatherNuvens,
      
      // Shower rain
      '09d': IconWeatherChuvaIntensa,
      '09n': IconWeatherChuvaIntensa,
      
      // Rain
      '10d': IconWeatherParcialmenteNubladoChuva,
      '10n': IconWeatherParcialmenteNubladoChuva,
      
      // Thunderstorm
      '11d': IconWeatherChuvaIntensa,
      '11n': IconWeatherChuvaIntensa,
      
      // Snow
      '13d': IconWeatherNuvens,
      '13n': IconWeatherNuvens,
      
      // Mist/Fog
      '50d': IconWeatherNuvens,
      '50n': IconWeatherNuvens,
    };
    
    // Fallback baseado no horário
    if (!weatherCode) {
      return isDayTime ? IconWeatherSol : IconWeatherNoiteParcialmenteNublada;
    }
    
    return iconMap[weatherCode] || (isDayTime ? IconWeatherSol : IconWeatherNoiteParcialmenteNublada);
  };

  // Rótulo do eixo do gráfico, a partir da data/hora JÁ convertidas pro fuso de
  // Brasília (ver formatterData/formatterHora em forecastByHour - date é
  // "YYYY-MM-DD", hour é 0-23, ambos America/Sao_Paulo, não UTC).
  const formatChartLabel = (date, hour, mobile) => {
    if (!date) return "";

    try {
      // Mobile: só a cada 6h (00h/06h/12h/18h), sem abreviação de dia - os
      // gráficos deixaram de rolar horizontal nessa largura (ver styles.js),
      // então mostram menos rótulos pra não amontoar no eixo. Os horários de
      // 3 em 3h que não são múltiplos de 6 (3,9,15,21) ficam sem rótulo (o
      // ponto continua no gráfico, só não ganha texto no eixo).
      if (mobile) {
        return hour % 6 === 0 ? `${String(hour).padStart(2, "0")}h` : "";
      }

      // Meia-noite (0h) local = início de um novo dia - mostra o dia da semana
      // em vez da hora, igual ao "seg" do semáforo climático.
      if (hour === 0) {
        const [yyyy, mm, dd] = date.split("-");
        const dateObj = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
        const weekdays = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
        return weekdays[dateObj.getDay()];
      }

      // Para as demais horas, mostra só o número (3, 6, 12, 18, 21)
      return String(hour);
    } catch (error) {
      console.error("Erro ao formatar label:", date, hour, error);
      return hour === 0 ? "" : String(hour);
    }
  };

  // Função para formatar dia da semana
  const getWeekday = (dateString) => {
    try {
      const [yyyy, mm, dd] = dateString.split("-");
      const dateObj = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
      const weekdays = ["Dom.", "Seg.", "Ter.", "Qua.", "Qui.", "Sex.", "Sáb."];
      return weekdays[dateObj.getDay()];
    } catch (error) {
      return "";
    }
  };

  // Função para formatar dia do mês
  const getDayOfMonth = (dateString) => {
    try {
      const [yyyy, mm, dd] = dateString.split("-");
      return parseInt(dd, 10);
    } catch (error) {
      return 0;
    }
  };

  // Função para formatar data no formato DD/MM/YYYY HH:MM:SS
  // Igual a formatChartLabel: recebe date/hour já no fuso de Brasília, não dt_txt
  // (UTC) cru - os passos da API são sempre em horas cheias (:00:00).
  const formatDateTimeBR = (date, hour) => {
    if (!date) return "";

    try {
      const [year, month, day] = date.split("-");
      const horaFormatada = String(hour).padStart(2, '0');

      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year} ${horaFormatada}:00:00`;
    } catch (error) {
      console.error("Erro ao formatar data:", date, hour, error);
      return date;
    }
  };

  // VERSÃO CORRIGIDA do useMemo para forecastByHour
  const forecastByHour = useMemo(() => {
    if (!weatherData?.list?.length) return [];

    // `item.dt_txt`/`item.dt` vêm em UTC (documentado pela OpenWeatherMap) - usar
    // `dt_txt` direto, como já era feito aqui, tratava a hora UTC como se já fosse
    // a de Brasília, adiantando o eixo do gráfico em 3h (mesmo bug já corrigido no
    // semáforo climático - ver weather-traffic-light/index.js). `item.dt` é o
    // timestamp Unix (UTC) - formatar ele já no fuso America/Sao_Paulo dá a
    // data/hora local corretas, inclusive quando a conversão muda o dia.
    const formatterData = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const formatterHora = new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      hour12: false,
    });

    const mapped = weatherData.list
      .filter((item) => typeof item?.dt_txt === "string" && item.dt)
      .map((item) => {
        const dataHoraUtc = new Date(item.dt * 1000);
        const date = formatterData.format(dataHoraUtc); // "YYYY-MM-DD" em America/Sao_Paulo
        const hour = parseInt(formatterHora.format(dataHoraUtc), 10); // 0-23 em America/Sao_Paulo

        // Formatar label conforme regras especificadas
        const label = formatChartLabel(date, hour, isMobile);

        // Extrair dados de precipitação
        // pop = Probability of Precipitation (0-1)
        const pop = item.pop != null ? Number((item.pop * 100).toFixed(0)) : 0; // Converter para porcentagem
        
        // Extrair volume de chuva em mm (se disponível)
        const rain3h = item.rain?.["3h"] != null ? Number(item.rain["3h"].toFixed(1)) : 0;
        const rain1h = item.rain?.["1h"] != null ? Number(item.rain["1h"].toFixed(1)) : 0;
        const rainVolume = rain3h || rain1h || 0;

        return {
          date,
          dt_txt: item.dt_txt,
          formattedDateTime: formatDateTimeBR(date, hour), // Adiciona campo com data formatada
          label,
          hour, // já é number (0-23, America/Sao_Paulo) - mantém a hora para filtros
          temp: item.main?.temp != null ? Number(item.main.temp.toFixed(1)) : 0,
          wind: item.wind?.speed != null ? Number((item.wind.speed * 3.6).toFixed(1)) : 0,
          humidity: item.main?.humidity != null ? Number(item.main.humidity) : 0,
          weatherIcon: item.weather?.[0]?.icon || '01d',
          weatherDescription: item.weather?.[0]?.description || 'céu limpo',
          // Dados de precipitação
          pop: pop, // Probabilidade de chuva em %
          rainVolume: rainVolume, // Volume de chuva em mm
          hasRain: rainVolume > 0 || pop > 0, // Flag para indicar se há chuva
        };
      });

    // Sem filtro de horário - mantém todos os passos de 3h que a API devolve
    // (0, 3, 6, 9, 12, 15, 18, 21), batendo com os mesmos horários exibidos na
    // Janela de pulverização (ver weather-traffic-light/index.js), que não
    // pulava nenhum. Filtrar aqui (antes: só 0/3/6/12/18/21) fazia esses
    // gráficos mostrarem menos horários que o semáforo.
    const filtered = mapped;


    // Agrupar por data para garantir que tenhamos todos os dias
    const groupedByDate = {};
    
    filtered.forEach(item => {
      if (!groupedByDate[item.date]) {
        groupedByDate[item.date] = [];
      }
      groupedByDate[item.date].push(item);
    });

    // Pegar todas as datas e ordená-las
    const allDates = Object.keys(groupedByDate).sort();
    
    // Limitar a 5 dias (hoje + próximos 4) - o dia seguinte costuma vir
    // incompleto (só umas poucas horas da madrugada), por isso é cortado em
    // vez de exibido pela metade.
    const daysToShow = 5;
    const selectedDates = allDates.slice(0, daysToShow);

    // Coletar todos os itens das datas selecionadas
    const result = [];
    selectedDates.forEach(date => {
      const items = groupedByDate[date];
      // Ordenar por hora para garantir ordem correta no gráfico
      items.sort((a, b) => a.hour - b.hour);
      result.push(...items);
    });

    return result;
  }, [weatherData, isMobile]);

  // Agrupar dados por dia para os PreviewDay
  const dailyForecast = useMemo(() => {
    if (!forecastByHour.length) return [];

    const groupedByDate = {};
    
    forecastByHour.forEach(item => {
      if (!groupedByDate[item.date]) {
        groupedByDate[item.date] = {
          date: item.date,
          dayOfMonth: getDayOfMonth(item.date),
          weekday: getWeekday(item.date),
          temps: [],
          weatherIcons: [],
          weatherDescriptions: [],
          popValues: [], // Probabilidade de chuva
          rainVolumes: [], // Volume de chuva
          dataPoints: []
        };
      }
      
      groupedByDate[item.date].temps.push(item.temp);
      groupedByDate[item.date].weatherIcons.push(item.weatherIcon);
      groupedByDate[item.date].weatherDescriptions.push(item.weatherDescription);
      groupedByDate[item.date].popValues.push(item.pop);
      groupedByDate[item.date].rainVolumes.push(item.rainVolume);
      groupedByDate[item.date].dataPoints.push(item);
    });

    // Converter para array e calcular máximos/mínimos
    return Object.values(groupedByDate).map(day => {
      const maxTemp = Math.max(...day.temps);
      const minTemp = Math.min(...day.temps);
      const maxPop = Math.max(...day.popValues);
      const avgRain = day.rainVolumes.reduce((a, b) => a + b, 0) / day.rainVolumes.length;
      
      // Determinar ícone mais frequente para o dia
      const iconCounts = {};
      day.weatherIcons.forEach(icon => {
        iconCounts[icon] = (iconCounts[icon] || 0) + 1;
      });
      
      const mostCommonIcon = Object.keys(iconCounts).reduce((a, b) => 
        iconCounts[a] > iconCounts[b] ? a : b
      );
      
      // Determinar descrição mais frequente para o dia
      const descCounts = {};
      day.weatherDescriptions.forEach(desc => {
        descCounts[desc] = (descCounts[desc] || 0) + 1;
      });
      
      const mostCommonDesc = Object.keys(descCounts).reduce((a, b) => 
        descCounts[a] > descCounts[b] ? a : b
      );
      
      return {
        ...day,
        maxTemp: Math.round(maxTemp),
        minTemp: Math.round(minTemp),
        maxPop: Math.round(maxPop),
        avgRain: Number(avgRain.toFixed(1)),
        icon: mostCommonIcon,
        description: mostCommonDesc,
      };
    });
  }, [forecastByHour]);

  /* ==========================
     BUSCA DOS DADOS
  ========================== */
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await axios.get(
          `https://api.openweathermap.org/data/2.5/forecast?q=${location}&units=metric&lang=pt&appid=${WEATHER_API_KEY}`
        );
        setWeatherDataInternal(response.data);
      } catch (error) {
        console.error("Erro ao buscar dados do clima:", error);
      }
    };

    const fetchInmet = async () => {
      try {
        const response = await apiprevmet3.get(`/previsao/${codIBGE}`);
        setInmetData(response.data);
      } catch (error) {
        console.error("Erro ao buscar dados INMET:", error);
      }
    };

    setIsLoading(true);

    // Só busca clima próprio se não recebeu weatherData via prop
    if (weatherDataProp === undefined && location) fetchWeather();
    if (codIBGE) fetchInmet();

    setIsLoading(false);
  }, [location, codIBGE, weatherDataProp]);

  /* ==========================
     FORMATAÇÃO DOS DADOS
  ========================== */
  const tempChartData = useMemo(() => {
    return forecastByHour;
  }, [forecastByHour]);

  const windChartData = useMemo(() => {
    return forecastByHour;
  }, [forecastByHour]);
  
  const humidityChartData = useMemo(() => {
    return forecastByHour;
  }, [forecastByHour]);

  const rainChartData = useMemo(() => {
    return forecastByHour;
  }, [forecastByHour]);

  // Função para calcular os ticks do eixo Y de 5 em 5
  const calculateYAxisTicks = (data, dataKey, minValue = null, maxValue = null) => {
    if (!data || data.length === 0) return [0, 5, 10, 15, 20, 25, 30, 35];
    
    let min = minValue !== null ? minValue : Math.floor(Math.min(...data.map(item => item[dataKey])) / 5) * 5;
    let max = maxValue !== null ? maxValue : Math.ceil(Math.max(...data.map(item => item[dataKey])) / 5) * 5;
    
    // Garantir que min seja pelo menos 0 para valores positivos
    if (min < 0) {
      min = Math.floor(min / 5) * 5;
    } else {
      min = Math.max(0, Math.floor(min / 5) * 5);
    }
    
    // Criar array de ticks de 5 em 5
    const ticks = [];
    for (let i = min; i <= max; i += 5) {
      ticks.push(i);
    }
    
    return ticks.length > 0 ? ticks : [0, 5, 10, 15, 20, 25, 30, 35];
  };

  // Função para calcular ticks para probabilidade de chuva (0-100%)
  const calculateRainTicks = () => {
    return [0, 20, 40, 60, 80, 100];
  };

  /* ==========================
     LOADING
  ========================== */
  if (isLoading) {
    return (
      <S.Container>
        <S.ContainerContent>
          <S.Loading />
        </S.ContainerContent>
      </S.Container>
    );
  }

  /* ==========================
     SEM DADOS
  ========================== */
  if (!tempChartData.length && !windChartData.length && !humidityChartData.length && !rainChartData.length) {
    return (
      <S.Container NoContent={true}>
        <S.ContainerContent>
          <img src={IconSearchNotFound} alt="Sem dados" />

          <TaglineP
            color={theme.colors.gray.Cinza_2}
            fontWeight={800}
          >
            Sem dados climáticos para exibição
          </TaglineP>
        </S.ContainerContent>
      </S.Container>
    );
  }

  // Customização do Tooltip para mostrar data formatada corretamente
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const iconUrl = `https://openweathermap.org/img/wn/${data.weatherIcon}@2x.png`;
      
      // formattedDateTime é sempre preenchido lá no forecastByHour (ver acima) -
      // este fallback só existe pra nunca quebrar se um dado vier incompleto.
      const displayDate = data.formattedDateTime || data.date || "";
      
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          minWidth: '200px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <img src={iconUrl} alt={data.weatherDescription} width="40" height="40" />
            <div style={{ marginLeft: '8px' }}>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{displayDate}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#666', textTransform: 'capitalize' }}>
                {data.weatherDescription}
              </p>
            </div>
          </div>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '4px 0' }}>
              {`${entry.name}: ${entry.value}${entry.dataKey === 'temp' ? '°C' : 
                entry.dataKey === 'wind' ? ' km/h' : 
                entry.dataKey === 'humidity' ? '%' : 
                entry.dataKey === 'pop' ? '%' :
                entry.dataKey === 'rainVolume' ? ' mm' : ''}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calcular ticks para cada gráfico
  const tempTicks = calculateYAxisTicks(tempChartData, 'temp', 0, 35);
  const windTicks = calculateYAxisTicks(windChartData, 'wind', 0, 35);
  const humidityTicks = calculateYAxisTicks(humidityChartData, 'humidity', 0, 100);
  const rainTicks = calculateRainTicks();

  /* ==========================
     RENDER
  ========================== */
  return (
    <S.Container>
      {address && (
        <S.AddressInfo>
          <TaglineP color={theme.colors.gray.Cinza_6} fontWeight={800}>
            Clima referente ao endereço da propriedade
          </TaglineP>
          <Auxiliar color={theme.colors.gray.Cinza_2}>{address}</Auxiliar>
        </S.AddressInfo>
      )}

      {/* Seção de Preview dos Próximos Dias */}
      {dailyForecast.length > 0 && (
        <S.ContainerPreviewDay>
          {dailyForecast.map((day, index) => {
            // Determinar label do dia
            let dayLabel = day.weekday;
            if (index === 0) {
              // Primeiro item é sempre hoje
              dayLabel = "Hoje";
            } else if (index === 1) {
              // Segundo item é amanhã
              dayLabel = "Amanhã";
            }
            
            // Determinar ícone baseado no código do tempo
            const hourForIcon = day.dataPoints[0]?.hour || 12;
            const weatherIcon = getWeatherIcon(day.icon, hourForIcon);

            return (
              <S.PreviewDay key={day.date}>
                <S.Day>
                  <BodyP color={theme.colors.gray.Cinza_5} fontWeight={800}>
                    {day.dayOfMonth}
                  </BodyP>

                  <BodyP color={theme.colors.gray.Cinza_5} fontWeight={600}>
                    {dayLabel}
                  </BodyP>
                </S.Day>

                <S.Preview>
                  <img 
                    src={weatherIcon} 
                    alt={day.description} 
                    style={{ width: '50px', height: '50px' }} 
                  />
                  
                  <S.PreviewModaInfo>
                    <p>{day.maxTemp}°</p> {/* Máxima Temperatura */}
                    <p>{day.minTemp}°</p> {/* Mínima Temperatura */}
                  </S.PreviewModaInfo>
                </S.Preview>
              </S.PreviewDay>
            );
          })}
        </S.ContainerPreviewDay>
      )}

      {afterDailyCards}

      <S.ContainerContentGraphic>
        {/* Gráfico de Temperatura */}
        <S.ContainerGraphicPreview>
          <BodyP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Temperatura
          </BodyP>

          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={tempChartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.colors.orange.Laranja} stopOpacity={1} />
                  <stop offset="50%" stopColor={theme.colors.yellow.Amarelo} stopOpacity={1} />
                  <stop offset="100%" stopColor={theme.colors.blue.Azul_link} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                // Fonte menor no mobile (ver formatChartLabel/isMobile) - com
                // só 1 rótulo a cada 6h, cada um ganha mais espaço, mas ainda
                // assim cabe melhor com a fonte reduzida.
                tick={{ fontSize: isMobile ? 9 : 11 }}
                interval={0}
                minTickGap={-10}
              />
              <YAxis
                domain={[0, 35]}
                ticks={tempTicks}
                tick={{ fontSize: 11 }}
                width={28}
              />
              <Tooltip content={<CustomTooltip />} />

              <Area
                type="monotone"
                dataKey="temp"
                stroke={theme.colors.orange.Laranja}
                fill="url(#tempGradient)"
                fillOpacity={0.16}
                strokeWidth={2}
                name="Temperatura"
              />
            </AreaChart>
          </ResponsiveContainer>
        </S.ContainerGraphicPreview>

        <RainForecastGraphic />

        <S.ContainerGraphicPreview>
          <BodyP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Probabilidade de chuva
          </BodyP>

          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={rainChartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="rainAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.colors.blue.Azul_link} stopOpacity={1} />
                  <stop offset="50%" stopColor={theme.colors.blue.Azul_link} stopOpacity={1} />
                  <stop offset="100%" stopColor={theme.colors.blue.Azul_link} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                // Fonte menor no mobile (ver formatChartLabel/isMobile) - com
                // só 1 rótulo a cada 6h, cada um ganha mais espaço, mas ainda
                // assim cabe melhor com a fonte reduzida.
                tick={{ fontSize: isMobile ? 9 : 11 }}
                interval={0}
                minTickGap={-10}
              />
              <YAxis
                domain={[0, 100]}
                ticks={rainTicks}
                tick={{ fontSize: 11 }}
                width={28}
              />
              <Tooltip 
                content={<CustomTooltip />}
                formatter={(value) => [`${value}%`, 'Probabilidade']}
              />
              <Area
                type="monotone"
                dataKey="pop"
                stroke={theme.colors.blue.Azul_link}
                fill="url(#rainAreaGradient)"
                fillOpacity={0.1}
                strokeWidth={2}
                name="Chuva"
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </S.ContainerGraphicPreview>

        {/* Gráfico de Velocidade do Vento */}
        <S.ContainerGraphicPreview>
          <BodyP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Velocidade do vento
          </BodyP>

          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={windChartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="windGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.colors.green.Folha} stopOpacity={1} />
                  <stop offset="30%" stopColor={theme.colors.green.Bandeira} stopOpacity={1} />
                  <stop offset="75%" stopColor={theme.colors.green.Verde_AgroReceita} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                // Fonte menor no mobile (ver formatChartLabel/isMobile) - com
                // só 1 rótulo a cada 6h, cada um ganha mais espaço, mas ainda
                // assim cabe melhor com a fonte reduzida.
                tick={{ fontSize: isMobile ? 9 : 11 }}
                interval={0}
                minTickGap={-10}
              />
              <YAxis
                domain={[0, 35]}
                ticks={windTicks}
                tick={{ fontSize: 11 }}
                width={28}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="wind"
                stroke={theme.colors.green.Bandeira}
                fill="url(#windGradient)"
                fillOpacity={0.16}
                strokeWidth={2}
                name="Vento"
              />
            </AreaChart>
          </ResponsiveContainer>
        </S.ContainerGraphicPreview>

        {/* Gráfico de Umidade */}
        <S.ContainerGraphicPreview>
          <BodyP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Umidade
          </BodyP>

          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={humidityChartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="humidityGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.colors.blue.Azul_link} stopOpacity={1} />
                  <stop offset="30%" stopColor={theme.colors.blue.Azul_link} stopOpacity={1} />
                  <stop offset="75%" stopColor={theme.colors.blue.Azul_link} stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                // Fonte menor no mobile (ver formatChartLabel/isMobile) - com
                // só 1 rótulo a cada 6h, cada um ganha mais espaço, mas ainda
                // assim cabe melhor com a fonte reduzida.
                tick={{ fontSize: isMobile ? 9 : 11 }}
                interval={0}
                minTickGap={-10}
              />
              <YAxis
                domain={[0, 100]}
                ticks={humidityTicks}
                tick={{ fontSize: 11 }}
                width={28}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="humidity"
                stroke={theme.colors.blue.Azul_link}
                fill="url(#humidityGradient)"
                fillOpacity={0.16}
                strokeWidth={2}
                name="Umidade"
              />
            </AreaChart>
          </ResponsiveContainer>
        </S.ContainerGraphicPreview>
      </S.ContainerContentGraphic>

      {/* Gráfico opcional: Volume de Chuva (se houver dados) */}
      {/* {rainChartData.some(item => item.rainVolume > 0) && (
        <S.ContainerGraphicPreview>
          <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Volume de chuva
          </TaglineP>

          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={rainChartData}>
              <defs>
                <linearGradient id="rainVolumeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E64A8" stopOpacity={0.8} />
                  <stop offset="50%" stopColor="#4A90E2" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#7DB9FF" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                // Fonte menor no mobile (ver formatChartLabel/isMobile) - com
                // só 1 rótulo a cada 6h, cada um ganha mais espaço, mas ainda
                // assim cabe melhor com a fonte reduzida.
                tick={{ fontSize: isMobile ? 9 : 11 }}
                interval={0}
                minTickGap={-10}
              />
              <YAxis 
                domain={[0, 'auto']}
                tick={{ fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="rainVolume"
                stroke="#1E64A8"
                fill="url(#rainVolumeGradient)"
                fillOpacity={1}
                strokeWidth={2}
                name="Chuva (mm)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </S.ContainerGraphicPreview>
      )} */}
    </S.Container>
  );
};

export default GraphicPreview;